// src/pages/Packs/components/PackEditMediaComponents/EffectSelectorModal.jsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';

const sliderConfig = {
  blur: {
    min: 5,
    max: 150,
    step: 1,
    labelKey: 'packs.edit_pack.effects_modal.intensityLabels.blur',
  },
  pixelate: {
    min: 5,
    max: 120,
    step: 2,
    labelKey: 'packs.edit_pack.effects_modal.intensityLabels.pixelate',
  },
  sketch: {
    min: 2,
    max: 30,
    step: 1,
    labelKey: 'packs.edit_pack.effects_modal.intensityLabels.sketch',
  },
  color_pixelate: {
    min: 5,
    max: 120,
    step: 2,
    labelKey: 'packs.edit_pack.effects_modal.intensityLabels.color_pixelate',
  },
};

const EffectSelectorModal = ({
  isOpen,
  onClose,
  displayImage,
  availableEffects,
  onSelectEffect,
  onConfirm,
  isPreviewing,
  previewState,
  onIntensityChange,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const currentEffect = previewState.effectType;
  const currentSlider = sliderConfig[currentEffect];

  return (
    // --- 1. SE AÑADE overflow-y-auto Y SE AJUSTA EL PADDING Y LA ALINEACIÓN ---
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4 pt-8 md:items-center md:pt-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-neutral-900 rounded-xl shadow-card p-6 max-w-4xl w-full flex flex-col md:flex-row gap-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Columna de Previsualización */}
        <div className="w-full md:w-1/2 flex flex-col items-center">
          <h4 className="font-bold text-neutral-800 dark:text-neutral-100 mb-3 text-center">
            {t('packs.edit_pack.effects_modal.previewTitle')}
          </h4>
          <div className="relative w-full aspect-square bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden border dark:border-neutral-700">
            {isPreviewing && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  className="text-white text-3xl"
                />
              </div>
            )}
            {displayImage ? (
              <img
                key={displayImage}
                src={displayImage}
                alt={t('packs.edit_pack.effects_modal.previewAlt')}
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>
        </div>

        {/* Columna de Opciones */}
        <div className="w-full md:w-1/2 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-neutral-800 dark:text-neutral-100">
              {t('packs.edit_pack.effects_modal.title')}
            </h3>
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded-full text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition"
              onClick={onClose}
              disabled={isPreviewing}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* --- 2. LA LISTA DE EFECTOS AHORA USA 2 COLUMNAS POR DEFECTO --- */}
          <div className="grid grid-cols-2 gap-3 max-h-[40vh] overflow-y-auto p-1 flex-grow">
            {Object.entries(availableEffects).map(([key, effect]) => (
              <button
                key={key}
                type="button"
                className={`p-3 rounded-lg border-2 transition-all text-left ${
                  currentEffect === key
                    ? 'border-primary bg-primary/5 dark:bg-primary/10 shadow-md ring-2 ring-primary'
                    : 'border-neutral-200 dark:border-neutral-700 hover:border-primary/50 dark:hover:border-primary/50 bg-white dark:bg-neutral-800 hover:shadow-lg'
                }`}
                onClick={() => onSelectEffect(key)}
                disabled={isPreviewing}
              >
                <div className="font-semibold text-neutral-800 dark:text-neutral-100">
                  {t(effect.nameKey) || effect.name}
                </div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  {t(effect.descriptionKey) || effect.description}
                </div>
              </button>
            ))}
          </div>

          {currentSlider && (
            <div className="mt-4 pt-4 border-t dark:border-neutral-700">
              <label
                htmlFor="intensity"
                className="block text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-2"
              >
                {t(currentSlider.labelKey)}: {previewState.intensity}
              </label>
              <input
                id="intensity"
                type="range"
                min={currentSlider.min}
                max={currentSlider.max}
                step={currentSlider.step}
                value={previewState.intensity}
                disabled={isPreviewing}
                onChange={(e) =>
                  onIntensityChange(parseInt(e.target.value, 10))
                }
                className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer dark:bg-neutral-700"
              />
            </div>
          )}

          <div className="mt-4 pt-4 border-t dark:border-neutral-700 flex gap-4">
            <button
              type="button"
              className="btn-secondary w-full"
              onClick={onClose}
              disabled={isPreviewing}
            >
              {t('packs.edit_pack.effects_modal.cancelButton')}
            </button>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={onConfirm}
              disabled={isPreviewing || !previewState.effectType}
            >
              {t('packs.edit_pack.effects_modal.confirmButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EffectSelectorModal;
